/**
 * 格式化 JSON 字符串用于展示
 * 如果是有效的 JSON 则格式化，否则返回原字符串
 * @param jsonStr JSON 字符串
 * @returns 格式化后的 JSON 字符串
 */
export function formatJSONForDisplay(jsonStr?: string): string {
  if (!jsonStr) {
    return '';
  }
  try {
    const parsed = JSON.parse(jsonStr);
    return JSON.stringify(parsed, null, 2);
  } catch {
    return jsonStr;
  }
}

/**
 * 安全的 JSON 解析函数
 * 解析失败时返回 null
 * @param json JSON 字符串
 * @returns 解析后的对象，失败返回 null
 */
export function safeJSONParse<T = any>(json?: string): T | null {
  if (!json) {
    return null;
  }
  try {
    return JSON.parse(json) as T;
  } catch {
    return null;
  }
}

/**
 * 归一化为数组，支持批量操作
 * 如果是数组则返回数组，如果是单个对象则包装成数组
 * @param data 单个对象或数组
 * @returns 数组
 */
export function normalizeToArray<T>(data: T | T[] | undefined | null): T[] {
  if (!data) {
    return [];
  }
  return Array.isArray(data) ? data : [data];
}

/**
 * 解码 JSON 字符串中的转义字符
 * @param str 包含转义字符的字符串
 * @returns 解码后的字符串
 */
export function decodeJSONString(str: string): string {
  return str
    .replace(/\\n/g, '\n')
    .replace(/\\t/g, '\t')
    .replace(/\\r/g, '\r')
    .replace(/\\"/g, '"')
    .replace(/\\\\/g, '\\');
}

/**
 * 修复 AI 生成的不规范 JSON（处理字符串值中未转义的双引号）
 * AI 有时会在 JSON 字符串值中直接放置未转义的英文双引号，导致 JSON.parse 失败。
 * 此函数通过识别结构性引号 vs 内容引号来修复此类问题。
 */
export function fixMalformedJSON(json: string): string {
  let result = '';
  let inString = false;
  let escape = false;

  for (let i = 0; i < json.length; i++) {
    const ch = json[i];

    if (escape) {
      result += ch;
      escape = false;
      continue;
    }

    if (ch === '\\') {
      result += ch;
      escape = true;
      continue;
    }

    if (ch === '"') {
      if (!inString) {
        inString = true;
        result += ch;
      } else {
        // 判断当前引号是关闭字符串的结构性引号还是内容引号
        const remaining = json.slice(i + 1).trimStart();
        const isStructural = remaining.length === 0 || /^[,\]}:]/.test(remaining);
        if (isStructural) {
          inString = false;
          result += ch;
        } else {
          // 内容引号，转义处理
          result += '\\"';
        }
      }
    } else {
      result += ch;
    }
  }

  return result;
}
